import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
    CustomerChannelListDto,
    GetCustomerChannelNamesRequest,
    GetCustomerChannelRequest,
    GetCustomerChannelsRequest,
    NameDTO,
    useCustomerChannelApi,
} from "api"
import { StoreUseQueryOptions } from "types"

export const customerChannelKeys = {
    all: ["customerChannel"] as const,
    lists: () => [...customerChannelKeys.all, "list"] as const,
    list: (param: GetCustomerChannelsRequest) => [...customerChannelKeys.lists(), param] as const,
    details: () => [...customerChannelKeys.all, "detail"] as const,
    detail: (param: GetCustomerChannelRequest) => [...customerChannelKeys.details(), param] as const,

    nameList: (param: GetCustomerChannelNamesRequest) => [...customerChannelKeys.lists(), "name", param] as const,
}

export const useCustomerChannel = (
    id: number,
    options: StoreUseQueryOptions<CustomerChannelListDto> = {
        notifyOnChangeProps: ["data", "isFetching", "isFetching"],
    }
) => {
    const api = useCustomerChannelApi()

    const param: GetCustomerChannelRequest = { id }
    return useQuery({
        queryKey: customerChannelKeys.detail(param),
        queryFn: () => api.getCustomerChannel(param),
        ...options,
    })
}
export const useCustomerChannelList = (
    param: GetCustomerChannelsRequest,
    options?: StoreUseQueryOptions<CustomerChannelListDto[]>
) => {
    const api = useCustomerChannelApi()

    return useQuery({
        queryKey: customerChannelKeys.list(param),
        queryFn: () => api.getCustomerChannels(param),
        ...options,
    })
}

export const useAddCustomerChannelMutation = () => {
    const api = useCustomerChannelApi()
    const queryClient = useQueryClient()

    return useMutation<any, any, any, any>({
        mutationFn: (d) => api.postCustomerChannel({ customerChannelDto: d }),

        onSettled: (_data, error, _variables) => {
            if (!error || error.status !== 400) {
                queryClient.invalidateQueries({ queryKey: customerChannelKeys.lists() })
            }
        },
    })
}

export const useUpdateCustomerChannelMutation = () => {
    const api = useCustomerChannelApi()
    const queryClient = useQueryClient()

    return useMutation<any, any, any, any>({
        mutationFn: (d) => api.putCustomerChannel({ id: d.id!, customerChannelUpdateDto: d }),

        onSettled: (_data, error, variables) => {
            if (!error || error.status !== 400) {
                queryClient.invalidateQueries({ queryKey: customerChannelKeys.detail({ id: variables.id! }) })
                queryClient.invalidateQueries({ queryKey: customerChannelKeys.lists() })
            }
        },
    })
}

export const useCustomerChannelNameList = (
    param: GetCustomerChannelNamesRequest,
    options: StoreUseQueryOptions<NameDTO[]> = {
        placeholderData: [],
        notifyOnChangeProps: ["data", "isFetching", "isFetching"],
    }
) => {
    const api = useCustomerChannelApi()

    return useQuery({
        queryKey: customerChannelKeys.nameList(param),
        queryFn: () => api.getCustomerChannelNames(param),
        ...options,
    })
}
